export type AppDesignUrlOverride = 'legacy' | 'modern';

const URL_OVERRIDE_PARAM = 'newDesign';

/**
 * Session storage, deliberately — not the local storage the user's saved preference lives in.
 * The override is meant to last for this document only, so writing it to the saved preference
 * would make a shared link permanently switch whoever opened it. It is stored at all only to
 * survive the OIDC redirect, which drops the query string on a cold sign-in, and is cleared as
 * soon as the user is authenticated.
 *
 * TODO: Remove the temporary newDesign URL override and OIDC handoff once the rollout no longer
 * needs the escape hatch.
 */
const URL_OVERRIDE_HANDOFF_KEY = 'app-design.url-override-handoff';

interface UrlOverrideHandoff {
  hash: string;
  mode: AppDesignUrlOverride;
  pathname: string;
}

export const getAppDesignUrlOverride = (search: string): AppDesignUrlOverride | undefined => {
  const value = new URLSearchParams(search).get(URL_OVERRIDE_PARAM);
  if (value === 'true') return 'modern';
  if (value === 'false') return 'legacy';
  return undefined;
};

export const clearAppDesignUrlOverrideHandoff = () => {
  try {
    window.sessionStorage.removeItem(URL_OVERRIDE_HANDOFF_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
};

export const writeAppDesignUrlOverrideHandoff = (mode: AppDesignUrlOverride, pathname: string, hash: string) => {
  try {
    const handoff: UrlOverrideHandoff = { mode, pathname, hash };
    window.sessionStorage.setItem(URL_OVERRIDE_HANDOFF_KEY, JSON.stringify(handoff));
  } catch {
    // The in-memory bootstrap state still preserves the override for this document.
  }
};

export const readAppDesignUrlOverrideHandoff = (pathname: string, hash: string): AppDesignUrlOverride | undefined => {
  try {
    const storedHandoff = window.sessionStorage.getItem(URL_OVERRIDE_HANDOFF_KEY);
    if (!storedHandoff) return undefined;

    const handoff = JSON.parse(storedHandoff) as Partial<UrlOverrideHandoff>;
    const isValidMode = handoff.mode === 'legacy' || handoff.mode === 'modern';
    if (isValidMode && handoff.pathname === pathname && handoff.hash === hash) {
      return handoff.mode;
    }

    clearAppDesignUrlOverrideHandoff();
  } catch {
    clearAppDesignUrlOverrideHandoff();
  }
  return undefined;
};

export const removeAppDesignUrlOverrideParam = (search: string) => {
  const searchParams = new URLSearchParams(search);
  searchParams.delete(URL_OVERRIDE_PARAM);
  return searchParams.toString();
};
